import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Leaf, HeartPulse, Salad, GraduationCap } from "lucide-react";
import Seo, { organizationJsonLd } from "../components/Seo";
import Reveal, { SectionTitle } from "../components/Reveal";
import Hero from "../components/home/Hero";
import BestSellers from "../components/home/BestSellers";
import Testimonials from "../components/home/Testimonials";
import { gallery, posts, site } from "../data/mock";
import { productLines, mooncake, cakeStudio, bakingClasses } from "../data/products";
import Img from "../components/Img";

const perks = [
  {
    icon: Leaf,
    title: "Nguyên liệu sạch",
    text: "Thực phẩm tươi chọn lọc mỗi sáng, không chất bảo quản trong mọi dòng sản phẩm.",
  },
  {
    icon: HeartPulse,
    title: "Chuẩn dinh dưỡng",
    text: "Món ăn ghi rõ calories, bánh giảm ngọt — vừa ngon vừa tốt cho cả nhà.",
  },
  {
    icon: Salad,
    title: "Làm theo đơn",
    text: "Bếp chỉ bắt đầu làm sau khi bạn đặt, không tồn kho, không hâm lại nhiều lần.",
  },
  {
    icon: GraduationCap,
    title: "Chia sẻ nghề",
    text: "Lớp nhỏ 4-6 người, bạn tự tay làm và mang công thức về nhà.",
  },
];

export default function Home() {
  return (
    <>
      <Seo
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            organizationJsonLd,
            {
              "@type": "WebSite",
              name: site.name,
              url: "https://annhome.com.vn/",
              inLanguage: "vi-VN",
            },
          ],
        }}
      />
      <Hero />

      {/* 4 dòng sản phẩm */}
      <section data-testid="product-lines" className="bg-white py-24" aria-labelledby="product-lines-title">
        <div className="mx-auto max-w-[1240px] px-6">
          <Reveal>
            <SectionTitle
              kicker="One kitchen, many stories"
              title="Bốn dòng sản phẩm của Ann Home"
              tag="h2"
            />
            <p className="ah-body mx-auto mt-6 max-w-[620px] text-center">
              Từ bữa ăn eat clean hằng ngày đến hộp bánh Trung thu mùa trăng, chiếc
              bánh kem sinh nhật và những buổi học làm bánh — tất cả đều ra đời từ
              một căn bếp Ann Home.
            </p>
          </Reveal>

          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {productLines.map((p, i) => (
              <Reveal key={p.id} delay={i * 120}>
                <Link
                  to={p.to}
                  className="ah-card group flex h-full flex-col border border-[#eceae2] bg-white transition-all duration-500 hover:-translate-y-2 hover:border-[#148456]/40 hover:shadow-[0_28px_54px_-34px_rgba(0,0,0,0.4)]"
                >
                  <div className="overflow-hidden">
                    <Img
                      src={p.image}
                      alt={`${p.name} - Ann Home`}
                      loading="lazy"
                      sizes="(min-width: 1024px) 297px, (min-width: 640px) calc(50vw - 40px), calc(100vw - 48px)"
                      className="ah-card-img h-[230px] w-full object-cover"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#8fa243]">
                      {p.kicker}
                    </p>
                    <h3 className="ah-heading mt-3 text-[18px] transition-colors duration-300 group-hover:text-[#148456]">
                      {p.name}
                    </h3>
                    <p className="ah-body mt-3 flex-1 !text-[13.5px]">{p.desc}</p>
                    <span className="mt-6 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#148456]">
                      {p.cta}
                      <ArrowRight
                        size={14}
                        className="transition-transform duration-300 group-hover:translate-x-1.5"
                      />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* perks */}
      <section className="ah-grain border-y border-[#efece4] bg-[#faf9f6] py-16">
        <div className="mx-auto grid max-w-[1240px] gap-10 px-6 sm:grid-cols-2 lg:grid-cols-4">
          {perks.map((p, i) => (
            <Reveal key={p.title} delay={i * 110}>
              <div className="group text-center">
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-[#e4e1d7] bg-white text-[#148456] transition-all duration-500 group-hover:-translate-y-1 group-hover:border-[#148456] group-hover:bg-[#148456] group-hover:text-white">
                  <p.icon size={24} strokeWidth={1.6} />
                </span>
                <h3 className="ah-heading mt-5 text-[15px]">{p.title}</h3>
                <p className="ah-body mt-2 !text-[13.5px]">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* brand story */}
      <section className="bg-white">
        <div className="mx-auto grid max-w-[1240px] items-center gap-14 px-6 py-24 lg:grid-cols-2">
          <Reveal>
            <div className="ah-card overflow-hidden">
              <Img
                src="/images/2017_01_cooking.webp"
                alt="Bếp trưởng Ann Home hoàn thiện món ăn"
                loading="lazy"
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="ah-card-img h-[400px] w-full object-cover sm:h-[470px]"
              />
            </div>
          </Reveal>
          <Reveal delay={140}>
            <div className="max-w-[540px]">
              <SectionTitle
                kicker="Our story"
                title="Ann Home mang đến những điều chất lượng cho bạn"
                align="left"
                tag="h2"
              />
              <p className="ah-body mt-7">
                Không chỉ là nơi ăn uống, Ann Home còn là nơi cung cấp kiến thức về
                dinh dưỡng và chế độ ăn uống lành mạnh, để bạn tự tin lựa chọn món
                phù hợp với nhu cầu của mình.
              </p>
              <p className="ah-body mt-4">
                Cùng một sự tử tế đó, chúng mình làm bánh Trung thu mỗi mùa trăng,
                làm bánh kem cho những buổi sinh nhật và mở lớp để chia sẻ lại nghề
                cho những người yêu bếp.
              </p>
              <Link
                to="/lien-he"
                className="group mt-8 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-[#148456]"
              >
                Liên hệ Ann Home
                <ArrowRight
                  size={15}
                  className="transition-transform duration-300 group-hover:translate-x-1.5"
                />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* mooncake feature */}
      <section
        data-testid="mooncake-feature"
        className="relative overflow-hidden bg-[#f7f2e8] py-24"
        aria-labelledby="mooncake-feature-title"
      >
        <div className="mx-auto grid max-w-[1240px] items-center gap-14 px-6 lg:grid-cols-[1.05fr_0.95fr]">
          <Reveal>
            <div className="ah-card overflow-hidden border border-[#e5dcc8]">
              <Img
                src={mooncake.cover}
                alt="Bộ sưu tập bánh Trung thu Ann Home Mộc Hương Thu Nguyệt 2026"
                loading="lazy"
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="ah-card-img w-full object-cover"
              />
            </div>
          </Reveal>
          <Reveal delay={140}>
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#a8763b]">
                {mooncake.year}
              </p>
              <h2
                id="mooncake-feature-title"
                className="ah-heading mt-3 text-[28px] leading-snug sm:text-[34px]"
                style={{ color: "#7a2020" }}
              >
                {mooncake.collection}
              </h2>
              <div className="mt-6 border-l-2 border-[#c89a5b] pl-5 text-[14.5px] font-light italic leading-[2] text-[#6b5a44]">
                {mooncake.poem.map((l) => (
                  <p key={l}>{l}</p>
                ))}
              </div>
              <p className="ah-body mt-6">{mooncake.intro}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                {mooncake.lines.map((l) => (
                  <span
                    key={l.id}
                    className="border border-[#d9c8a6] bg-white/70 px-4 py-2 text-[12px] tracking-wide text-[#7a5a2a]"
                  >
                    {l.name}
                  </span>
                ))}
              </div>
              <Link to="/banh-trung-thu" className="ah-btn ah-btn-solid mt-9">
                Xem bộ sưu tập
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <BestSellers />

      {/* cake studio feature */}
      <section data-testid="cake-studio" className="bg-white py-24" aria-labelledby="cake-feature-title">
        <div className="mx-auto max-w-[1240px] px-6">
          <Reveal>
            <SectionTitle
              kicker={cakeStudio.name}
              title="Bánh kem hiện đại, làm tươi mỗi ngày"
              tag="h2"
            />
            <p className="ah-body mx-auto mt-6 max-w-[620px] text-center">
              {cakeStudio.intro}
            </p>
          </Reveal>
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {cakeStudio.products.slice(0, 4).map((c, i) => (
              <Reveal key={c.id} delay={i * 110}>
                <article className="ah-card group h-full border border-[#eceae2] bg-white transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_26px_50px_-32px_rgba(0,0,0,0.32)]">
                  <div className="relative overflow-hidden">
                    <Img
                      src={c.image}
                      alt={`${c.name} - ${cakeStudio.name}`}
                      loading="lazy"
                      className="ah-card-img h-[220px] w-full object-cover"
                    />
                    {c.badge && (
                      <span className="absolute left-4 top-4 bg-[#148456] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-white">
                        {c.badge}
                      </span>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="ah-heading text-[15.5px]">{c.name}</h3>
                    <p className="mt-1.5 text-[12.5px] font-light text-[#8a8a82]">{c.meta}</p>
                    <p className="mt-3 text-[15px] font-bold text-[#e72d43]">{c.price}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal delay={160}>
            <div className="mt-14 text-center">
              <Link to="/banh-kem" className="ah-btn ah-btn-dark-outline">
                Xem tất cả bánh kem
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* baking class feature */}
      <section data-testid="baking-class" className="ah-grain bg-[#faf9f6] py-24" aria-labelledby="class-feature-title">
        <div className="mx-auto grid max-w-[1240px] items-center gap-14 px-6 lg:grid-cols-2">
          <Reveal className="lg:order-2">
            <div className="ah-card overflow-hidden">
              <Img
                src={bakingClasses.hero}
                alt="Lớp dạy làm bánh Ann Home tại TP.HCM"
                loading="lazy"
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="ah-card-img h-[400px] w-full object-cover"
              />
            </div>
          </Reveal>
          <Reveal delay={140} className="lg:order-1">
            <div className="max-w-[540px]">
              <SectionTitle
                kicker="Baking class"
                title="Học làm bánh cùng bếp trưởng Ann Home"
                align="left"
                tag="h2"
              />
              <p className="ah-body mt-7">{bakingClasses.intro}</p>
              <ul className="mt-7 grid gap-3 sm:grid-cols-2">
                {bakingClasses.courses.map((c) => (
                  <li
                    key={c.id}
                    className="border border-[#e6e3db] bg-white px-4 py-3 text-[13.5px] text-[#4a4a4a]"
                  >
                    <span className="block font-bold text-[#2f3a33]">{c.name}</span>
                    <span className="mt-1 block text-[12px] text-[#8a8a82]">
                      {`${c.duration} · ${c.price}`}
                    </span>
                  </li>
                ))}
              </ul>
              <Link to="/lop-hoc-lam-banh" className="ah-btn ah-btn-solid mt-9">
                Xem khoá học
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <Testimonials />

      {/* blog teaser */}
      <section data-testid="blog-teaser" className="bg-white py-24" aria-labelledby="blog-title">
        <div className="mx-auto max-w-[1240px] px-6">
          <Reveal>
            <SectionTitle kicker="Kiến thức" title="Ăn uống & làm bánh" tag="h2" />
          </Reveal>
          <div className="mt-14 grid gap-9 md:grid-cols-3">
            {posts.slice(0, 3).map((p, i) => (
              <Reveal key={p.slug} delay={i * 130}>
                <article className="ah-card group h-full border border-[#eceae2] bg-white transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_26px_50px_-32px_rgba(0,0,0,0.35)]">
                  <Link to={`/${p.slug}`} className="block overflow-hidden">
                    <Img
                      src={p.image}
                      alt={p.title}
                      loading="lazy"
                      className="ah-card-img h-[220px] w-full object-cover"
                    />
                  </Link>
                  <div className="p-7">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#8fa243]">
                      {p.category}
                    </p>
                    <h3 className="ah-heading mt-3 text-[16px] leading-relaxed">
                      <Link
                        to={`/${p.slug}`}
                        className="transition-colors duration-300 hover:text-[#148456]"
                      >
                        {p.title}
                      </Link>
                    </h3>
                    <p className="ah-body mt-3 !text-[13.5px] line-clamp-3">{p.excerpt}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal delay={160}>
            <div className="mt-14 text-center">
              <Link to="/danh-muc/kien-thuc" className="ah-btn ah-btn-dark-outline">
                Xem tất cả bài viết
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* gallery */}
      <section data-testid="home-gallery" aria-label="Thư viện hình ảnh Ann Home">
        <div className="grid grid-cols-2 lg:grid-cols-4">
          {gallery.map((g) => (
            <div key={g.src} className="ah-card group relative overflow-hidden">
              <Img
                src={g.src}
                alt={g.alt}
                loading="lazy"
                className="ah-card-img h-[240px] w-full object-cover"
              />
              <div className="absolute inset-0 bg-[#26302a]/0 transition-all duration-500 group-hover:bg-[#26302a]/45" />
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#f6faf7] py-20">
        <div className="mx-auto max-w-[820px] px-6 text-center">
          <Reveal>
            <p className="ah-kicker">Order now</p>
            <h2 className="ah-heading mt-1 text-[26px] sm:text-[32px]">
              Bạn muốn đặt gì hôm nay?
            </h2>
            <p className="ah-body mx-auto mt-5 max-w-[560px]">
              Nhắn tin cho Ann Home để được tư vấn thực đơn eat clean, set quà Trung
              thu, mẫu bánh kem hoặc lịch lớp học phù hợp với bạn.
            </p>
            <div className="mt-9 flex flex-col justify-center gap-4 sm:flex-row">
              <Link to="/lien-he" className="ah-btn ah-btn-solid">
                Gửi yêu cầu
              </Link>
              <a href={site.phoneHref} className="ah-btn ah-btn-dark-outline">
                {`Gọi ${site.phone}`}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
